import { useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import type { Brand } from '../types';

interface ContactMessage {
  id: number;
  brand_id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  status: string;
  ip_address: string;
  created_at: string;
}

export function ContactMessagesPage() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [brandFilter, statusFilter]);

  const fetchBrands = async () => {
    try {
      const res = await fetch(getApiUrl('/api/brands'), { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBrands(data.brands || []);
      }
    } catch {}
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (brandFilter) params.set('brand', brandFilter);
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', '100');

      const res = await fetch(getApiUrl(`/api/contact?${params}`), { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: number, status: string) => {
    try {
      await fetch(getApiUrl(`/api/contact/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      setMessages(prev => prev.map(m => m.id === id ? { ...m, status } : m));
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Contact Messages</h1>
          {unreadCount > 0 && (
            <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>
        <p className="text-dark-text-secondary text-sm sm:text-base">
          Messages submitted via the Contact Us form.
        </p>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6 flex flex-wrap gap-3">
        <select
          value={brandFilter}
          onChange={e => setBrandFilter(e.target.value)}
          className="bg-dark-bg border border-dark-border text-white rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Brands</option>
          {brands.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-dark-bg border border-dark-border text-white rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
        </select>

        <button
          onClick={fetchMessages}
          className="btn btn-secondary text-sm"
        >
          Refresh
        </button>
      </div>

      {/* Messages List */}
      <div className="space-y-3">
        {loading ? (
          <div className="card p-8 text-center text-dark-text-secondary">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="card p-8 text-center text-dark-text-secondary">No messages found</div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.id}
              className={`card p-4 cursor-pointer transition-colors ${
                msg.status === 'unread' ? 'border-l-4 border-blue-500' : ''
              }`}
              onClick={() => {
                setExpanded(expanded === msg.id ? null : msg.id);
                if (msg.status === 'unread') updateStatus(msg.id, 'read');
              }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white">{msg.name}</span>
                    <span className="text-dark-text-secondary text-sm">&lt;{msg.email}&gt;</span>
                    {msg.phone && (
                      <span className="text-dark-text-secondary text-sm">{msg.phone}</span>
                    )}
                  </div>
                  <p className="text-sm text-dark-text-secondary mt-1 truncate">
                    {msg.message}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                  <span className="text-xs text-dark-text-secondary capitalize">
                    {msg.brand_id.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    msg.status === 'unread' ? 'bg-blue-900/50 text-blue-400' :
                    msg.status === 'read' ? 'bg-gray-700 text-gray-400' :
                    'bg-green-900/50 text-green-400'
                  }`}>
                    {msg.status}
                  </span>
                  <span className="text-xs text-dark-text-secondary">
                    {new Date(msg.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Expanded view */}
              {expanded === msg.id && (
                <div className="mt-4 pt-4 border-t border-dark-border" onClick={e => e.stopPropagation()}>
                  <div className="bg-dark-bg rounded-lg p-4 text-sm text-white whitespace-pre-wrap mb-4">
                    {msg.message}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <a
                      href={`mailto:${msg.email}`}
                      className="btn btn-primary text-sm"
                    >
                      Reply via Email
                    </a>
                    {msg.status !== 'replied' && (
                      <button
                        onClick={() => updateStatus(msg.id, 'replied')}
                        className="btn btn-secondary text-sm"
                      >
                        Mark as Replied
                      </button>
                    )}
                    {msg.status !== 'unread' && (
                      <button
                        onClick={() => updateStatus(msg.id, 'unread')}
                        className="btn btn-secondary text-sm"
                      >
                        Mark as Unread
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
